import { useEffect, useState } from "react";

import { Button, Popover } from "antd";

import { LogoutOutlined, UserOutlined } from "@ant-design/icons";
import { RiFullscreenExitLine, RiFullscreenLine } from "react-icons/ri";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useDispatch } from "react-redux";
import { AutoComplete, Input, Select } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useGlobalSearchQuery } from "../redux/rtk/features/CRM/globalSearch/globalSearchApi";
import LanguageSwitcher from "../components/CommonUi/LanguageSwitcher";
import NotificationCenter from "../components/CommonUi/NotificationCenter";
import { logOut } from "../redux/rtk/features/hrm/user/userSlice";

const toggler = [
  <svg
    width="20"
    height="20"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 448 512"
    key={0}>
    <path d="M16 132h416c8.837 0 16-7.163 16-16V76c0-8.837-7.163-16-16-16H16C7.163 60 0 67.163 0 76v40c0 8.837 7.163 16 16 16zm0 160h416c8.837 0 16-7.163 16-16v-40c0-8.837-7.163-16-16-16H16c-8.837 0-16 7.163-16 16v40c0 8.837 7.163 16 16 16zm0 160h416c8.837 0 16-7.163 16-16v-40c0-8.837-7.163-16-16-16H16c-8.837 0-16 7.163-16 16v40c0 8.837 7.163 16 16 16z"></path>
  </svg>,
];

function Header({ onPress, data, loading, isSideNavNan }) {
  const { t } = useTranslation();
  const userId = localStorage.getItem("id");
  const isLogged = localStorage.getItem("isLogged");
  const user = localStorage.getItem("user");
  const element = document.documentElement;
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const isAgent = location.pathname.startsWith("/agents");

  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("all");
  const [searchField, setSearchField] = useState("all");
  const { data: searchResults, isFetching: searchLoading } = useGlobalSearchQuery(
    { key: searchTerm, type: searchType, field: searchField },
    {
      skip: !searchTerm || searchTerm.length < 2,
    }
  );

  const [resultsList, setResultsList] = useState([]);

  useEffect(() => {
    if (!searchTerm || searchTerm.length < 2) {
      setResultsList([]);
    } else if (searchLoading) {
      setResultsList([]);
    } else if (searchResults) {
      setResultsList(searchResults);
    }
  }, [searchTerm, searchResults, searchLoading]);

  useEffect(() => { window.scrollTo(0, 0); });

  const [fullScreen, setFullScreen] = useState(false);
  const [imageError, setImageError] = useState();

  const handleFullScreenClick = () => {
    setFullScreen(true);
    if (element.requestFullscreen) {
      element.requestFullscreen();
    } else if (element.mozRequestFullScreen) {
      element.mozRequestFullScreen();
    } else if (element.webkitRequestFullscreen) {
      element.webkitRequestFullscreen();
    } else if (element.msRequestFullscreen) {
      element.msRequestFullscreen();
    }
  };
  const handleExitFullScreenClick = () => {
    setFullScreen(false);
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setFullScreen(false);
      }
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  const handleLogout = () => {
    dispatch(logOut());
  };

  const onSelect = (value, option) => {
    setSearchTerm("");
    if (option.type === "deal") {
      navigate(`/admin/deal/${option.id}`);
    } else if (option.type === "lead") {
      navigate(`/admin/lead/${option.id}`);
    } else if (option.type === "contact") {
      navigate(`/admin/contact/${option.id}`);
    }
  };

  const options = resultsList?.map((item) => {
    let badgeClass = "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400";
    let typeLabel = t("common.lead");

    if (item.type === "deal") {
      badgeClass = "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400";
      typeLabel = t("common.deal");
    } else if (item.type === "contact") {
      badgeClass = "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400";
      typeLabel = t("common.contact");
    }

    return {
      label: (
        <div className="flex justify-between items-center py-1">
          <span className="font-medium text-gray-800 dark:text-gray-200">{item.name}</span>
          <span className={`text-xs px-2 py-0.5 rounded ${badgeClass}`}>
            {typeLabel}
          </span>
        </div>
      ),
      value: item.name,
      id: item.id,
      type: item.type,
    };
  }) || [];

  return (
    <>
      <div className="sticky top-0 z-10 flex w-full justify-between items-center py-3 pl-4 bg-white dark:bg-[#1C1B20] border-b ">
        <div className="relative flex items-center">
          {isLogged && !isSideNavNan && (
            <Button
              type="link"
              className="block md:hidden"
              onClick={() => onPress()}
              style={{ boxShadow: "none" }}>
              {toggler}
            </Button>
          )}
        </div>

        {isLogged && (
          <div className="hidden md:flex flex-1 items-center max-w-xl mx-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-[#252429] p-0.5 focus-within:border-blue-400 dark:focus-within:border-blue-500 transition-all">
            <Select
              value={searchType}
              onChange={(val) => setSearchType(val)}
              bordered={false}
              className="w-[100px] text-xs font-semibold text-gray-600 dark:text-gray-300"
              popupClassName="dark:bg-[#1C1B20]"
              options={[
                { value: "all", label: t("common.all") || "All" },
                { value: "lead", label: t("common.lead") || "Lead" },
                { value: "deal", label: t("common.deal") || "Deal" },
                { value: "contact", label: t("common.contact") || "Contact" },
              ]}
            />
            <div className="h-6 w-[1px] bg-gray-200 dark:bg-gray-700" />
            <Select
              value={searchField}
              onChange={(val) => setSearchField(val)}
              bordered={false}
              className="w-[130px] text-xs font-semibold text-gray-600 dark:text-gray-300"
              popupClassName="dark:bg-[#1C1B20]"
              options={[
                { value: "all", label: t("common.all_fields") || "All Fields" },
                { value: "name", label: t("common.name") || "Name" },
                { value: "phone", label: t("common.phone") || "Phone" },
                { value: "country", label: t("common.country") || "Country" },
                { value: "country_of_study", label: t("common.country_of_study") || "Country of Study" },
              ]}
            />
            <div className="h-6 w-[1px] bg-gray-200 dark:bg-gray-700" />
            <AutoComplete
              popupMatchSelectWidth={300}
              style={{ flex: 1 }}
              options={options}
              onSelect={onSelect}
              onSearch={(value) => setSearchTerm(value)}
              value={searchTerm}
              notFoundContent={
                searchLoading ? (
                  <div className="flex justify-center items-center py-2 text-gray-400 text-sm">
                    {t("common.loading") || "Loading..."}
                  </div>
                ) : undefined
              }
            >
              <Input
                size="middle"
                bordered={false}
                placeholder={t("common.search")}
                prefix={<SearchOutlined className="text-gray-400" />}
                className="bg-transparent"
              />
            </AutoComplete>
          </div>
        )}

        <div className="flex items-center mr-[1rem] gap-2 md:gap-3">
          {isLogged && <LanguageSwitcher />}

          {isLogged && (
            <Button
              type="text"
              onClick={fullScreen ? handleExitFullScreenClick : handleFullScreenClick}
              className="flex items-center justify-center h-10 w-10 p-0 bg-white dark:bg-[#1C1B20] border border-gray-100 dark:border-gray-700 rounded-lg shadow-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
              icon={
                fullScreen ? (
                  <RiFullscreenExitLine size={20} className="dark:text-white" />
                ) : (
                  <RiFullscreenLine size={20} className="dark:text-white" />
                )
              }
            />
          )}

          {isLogged && <NotificationCenter />}

          {isLogged && (
            <Popover
              placement="rightBottom"
              trigger="click"
              content={
                <div className="min-w-[150px] rounded-lg">
                  <p className="flex items-center gap-1  px-3 py-2  hover:bg-slate-200 rounded-lg">
                    <UserOutlined style={{ fontSize: "16px" }} />{" "}
                    <Link
                      to={isAgent ? `/agents/profile` : `/admin/staff/${userId}`}
                      className="logout-text uppercase font-weight-bold me-2 ms-1">
                      {user}
                    </Link>
                  </p>

                  <div
                    onClick={handleLogout}
                    className="flex items-center gap-1 px-3 py-2 hover:bg-slate-200 rounded-lg cursor-pointer">
                    <LogoutOutlined className=" text-red-500" />
                    <span className="logout-text font-weight-bold">
                      {t("auth.logout")}
                    </span>
                  </div>
                </div>
              }
              arrow={false}>
              <Button
                type="text"
                icon={<UserOutlined className="text-xl" />}
                className="flex items-center justify-center h-10 w-10 p-0 bg-white dark:bg-[#1C1B20] border border-gray-100 dark:border-gray-700 rounded-lg shadow-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
              />
            </Popover>
          )}
        </div>
      </div>
    </>
  );
}

export default Header;
